// Composants de page communs (même langage visuel que teqa_confirmation :
// page-header-card, metric-card, surface-card, badges pastel, états vides).
import { AlertCircle, Loader2, RefreshCw } from "lucide-react";
import { getApiErrorMessage } from "../../../ui/Feedback";

export const TONES = {
  success: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400",
  warning: "bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400",
  danger: "bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-400",
  info: "bg-sky-50 text-sky-700 dark:bg-sky-500/10 dark:text-sky-400",
  brand: "bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-400",
  neutral: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
};

export function PageHeader({ eyebrow, title, subtitle, actions }) {
  return (
    <div className="page-header-card flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:flex-row md:items-center md:justify-between">
      <div>
        {eyebrow ? <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400">{eyebrow}</div> : null}
        <h1 className="mt-1 text-2xl font-bold text-slate-900 dark:text-slate-100">{title}</h1>
        {subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export function MetricCard({ icon: Icon, label, value, hint, tone = "brand" }) {
  return (
    <div className="metric-card flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
      {Icon ? (
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${TONES[tone]}`}>
          <Icon className="h-5 w-5" />
        </div>
      ) : null}
      <div className="min-w-0">
        <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400">{label}</div>
        <div className="truncate text-xl font-bold text-slate-900 dark:text-slate-100">{value}</div>
        {hint ? <div className="text-xs text-slate-500">{hint}</div> : null}
      </div>
    </div>
  );
}

export function StatusPill({ tone = "neutral", children }) {
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${TONES[tone] || TONES.neutral}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
      {children}
    </span>
  );
}

export function FilterTabs({ tabs, value, onChange }) {
  return (
    <div className="flex gap-1 overflow-x-auto rounded-full border border-slate-200 bg-white p-1 scrollbar-none dark:border-slate-800 dark:bg-slate-900">
      {tabs.map((tab) => {
        const active = tab.key === value;
        return (
          <button key={tab.key} type="button" onClick={() => onChange(tab.key)}
            className={`inline-flex shrink-0 items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition ${active ? "bg-indigo-50 text-indigo-700" : "text-slate-500 hover:text-slate-900 dark:hover:text-slate-100"}`}>
            {tab.label}
            {tab.count !== undefined ? (
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${active ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"}`}>
                {tab.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

export function LoadingState({ label = "Chargement…" }) {
  return (
    <div className="flex h-64 items-center justify-center gap-2 text-sm text-slate-500">
      <Loader2 className="h-5 w-5 animate-spin text-indigo-600" /> {label}
    </div>
  );
}

export function ErrorState({ error, title = "Chargement impossible", onRetry }) {
  // les hooks stockent soit l'erreur axios, soit err.response.data, soit un texte
  const asAxios = error && typeof error === "object" && !error.response ? { response: { data: error } } : error;
  const message = typeof error === "string" ? error : getApiErrorMessage(asAxios, "Le service ne répond pas pour le moment.");
  return (
    <div className="rounded-xl border border-rose-200 bg-rose-50 p-6 text-center">
      <AlertCircle size={28} className="mx-auto mb-2 text-rose-500" />
      <p className="font-semibold text-rose-700">{title}</p>
      <p className="mt-1 text-sm text-rose-700/80">{message}</p>
      {onRetry ? (
        <button type="button" onClick={onRetry} className="btn-outline mt-3">
          <RefreshCw className="h-4 w-4" /> Réessayer
        </button>
      ) : null}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, message, action }) {
  return (
    <div className="empty-state flex flex-col items-center gap-2 rounded-lg bg-slate-50 p-10 text-center dark:bg-slate-800">
      {Icon ? <Icon className="h-8 w-8 text-slate-300" /> : null}
      <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">{title}</p>
      {message ? <p className="max-w-md text-xs text-slate-500">{message}</p> : null}
      {action}
    </div>
  );
}

export const formatMoney = (value, currency = "MAD") =>
  `${Number(value || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;

export const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "—";
